import { Add } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';

import 'react-image-crop/dist/ReactCrop.css';
import { AVATAR_MAX_SIZE } from '../../../constants/workspaces';
import Button from '../../atoms/button';

const centerAspectCrop = (mediaWidth, mediaHeight, aspect) => (
  centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
);

const AvatarUploadAndCropModal = (props) => {
  // init
  const { isOpen, setIsOpen, setAvatar, isUploading, setIsUploading } = props;

  // states
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [chosenImage, setChosenImage] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [error, setError] = useState();
  const [fileExtension, setFileExtension] = useState();

  const onSelectFile = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file.size <= AVATAR_MAX_SIZE) {
        setFileExtension(file.type.split('/')[1]);
        setError(undefined);
        setCrop(undefined); // Makes crop preview update between images.
        const reader = new FileReader();
        reader.addEventListener('load', () => setChosenImage(reader.result?.toString() || ''));
        reader.readAsDataURL(file);
      } else {
        setError('Maximum file size is 1MB. Please choose a smaller file.');
      }
    }
  };

  const onImageLoad = (event) => {
    const { width, height } = event.currentTarget;
    setCrop(centerAspectCrop(width, height, 1));
  };

  const handleClose = async () => {
    setIsOpen(false);
    setChosenImage(undefined);
  };

  const convertCroppedImageToBlob = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const image = imgRef.current;

    if (!ctx) {
      throw new Error('No 2d context');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    // devicePixelRatio slightly increases sharpness on retina devices
    // at the expense of slightly slower render times and needing to
    // size the image back down if you want to download/upload and be
    // true to the images natural size.
    const pixelRatio = window.devicePixelRatio;

    canvas.width = Math.floor(completedCrop.width * scaleX * pixelRatio);
    canvas.height = Math.floor(completedCrop.height * scaleY * pixelRatio);

    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = 'high';

    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;

    const centerX = image.naturalWidth / 2;
    const centerY = image.naturalHeight / 2;

    // Move the crop origin to the canvas origin (0,0)
    ctx.translate(-cropX, -cropY);
    // Move the origin to the center of the original position
    ctx.translate(centerX, centerY);
    // Move the center of the image to the origin (0,0)
    ctx.translate(-centerX, -centerY);
    // Draw the image
    ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, image.naturalWidth, image.naturalHeight);

    canvas.toBlob(async (blob) => {
      if (setIsUploading) setIsUploading(true);
      await setAvatar({
        blob,
        dataURI: URL.createObjectURL(blob),
        type: fileExtension,
      });
      setChosenImage(undefined);
      if (setIsUploading) setIsUploading(false);
      handleClose();
    });
  };

  const handleUpload = async () => {
    if (completedCrop) {
      convertCroppedImageToBlob();
    }
  };

  return (
    <Dialog className="custom-modal-dialog image-cropper-dialog" open={isOpen} onClose={handleClose}>
      <DialogTitle className="responsive-dialog-title">Choose Image</DialogTitle>
      <DialogContent>
        <div className="cropper-container">
          <div className="choose-file-layout">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="file-upload">
              <Button primary outlined component="span">
                <Add />
                Choose Image
              </Button>
            </label>
            <input
              id="file-upload"
              type="file"
              accept="image/jpeg, image/png, image/jpg"
              style={{ display: 'none' }}
              onChange={onSelectFile}
            />
          </div>
          {!!error && <div style={{ textAlign: 'center' }} className="error-message">{error}</div>}
          {!!chosenImage && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1}
            >
              <img
                ref={imgRef}
                alt="Crop preview"
                src={chosenImage}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
        </div>
      </DialogContent>
      <DialogActions className="dialog-actions">
        <div className="dialog-actions">
          <Button disabled={isUploading} primary outlined onClick={handleClose}>Cancel</Button>
          <Button isLoading={isUploading} disabled={isUploading} primary onClick={handleUpload}>Upload</Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

AvatarUploadAndCropModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  setAvatar: PropTypes.func.isRequired,
  isUploading: PropTypes.bool,
  setIsUploading: PropTypes.func,
};

export default AvatarUploadAndCropModal;
